import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { preferencesApi } from '../services/api';
import { useAuth } from './AuthContext';
import {
  DEBUG_HOVER_TOOLS, DEBUG_OVERLAY_TOOLS, SHOW_DEBUG_KEY, CSS_VARS_MODE_KEY,
  syncHoverState, syncOverlayTool, notifyDebugChanged,
} from '../utils/debugOverlay';

/**
 * PreferencesContext — THE single read/write path for user preferences.
 *
 * Server (AppSec_UserPreferences via /api/preferences) is the authority.
 * localStorage keeps two mirror roles only:
 *   1. Boot cache ('dawn.prefs.cache') — instant paint before hydration.
 *   2. Imperative mirrors (dawn.uiTheme, dawn.debug.*, ...) — legacy keys that
 *      non-React code (App.js boot appliers, debugOverlay.js sync fns) reads
 *      synchronously. The context writes INTO them; nothing else should.
 *
 * Resolution precedence (client-side, mirrors PreferenceResolver.cs):
 *   user+machine → user+db → user+global →
 *   default(role+db) → default(role) → default(db) → default(system) → catalog
 */

const CACHE_KEY = 'dawn.prefs.cache';
const LAST_USER_KEY = 'dawn.prefs.lastUserId';
const FLUSH_DEBOUNCE_MS = 1500;

// Role precedence — mirrors highestRole() in AuthContext.js
const ROLE_ORDER = ['SysAdmin', 'AppAdmin', 'PowerUser', 'User', 'Guest'];

// ── machineId (same key + create-if-absent semantics as DataTransferContext) ──
const genUUID = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
};
export const getMachineId = () => {
  let id = localStorage.getItem('dawn.machineId');
  if (!id) { id = genUUID(); localStorage.setItem('dawn.machineId', id); }
  return id;
};

// ── Legacy localStorage mirrors (imperative readers depend on these) ─────────
const debugMirrors = {};
DEBUG_HOVER_TOOLS.forEach(t => { debugMirrors[`debug.hover.${t.key}`] = t.lsKey; });
DEBUG_OVERLAY_TOOLS.forEach(t => { debugMirrors[`debug.overlay.${t.key}`] = t.lsKey; });

export const MIRROR_KEYS = {
  'display.theme': 'dawn.uiTheme',
  'display.uiZoom': 'dawn.uiZoom',
  'security.sessionTimeout': 'pmd-security.sessionTimeout',
  'debug.showFooterPanel': SHOW_DEBUG_KEY,
  'debug.logging': 'dawn.debugMode',
  'debug.cssVarsMode': CSS_VARS_MODE_KEY,
  ...debugMirrors,
};

// Dead keys from killed preferences — swept on migration, never imported.
const SWEEP_PREFIXES = ['pmd-locale.', 'pmd-autofill.', 'pmd-sub.'];
const SWEEP_KEYS = ['activityTracker.concurrencyLimit', 'pmd-security.screenRestrict'];

// ── Theme / zoom appliers (shared with App.js boot path + Footer) ─────────────
const THEME_CLASSES = ['dawn-theme-dark', 'dawn-theme-dim', 'dawn-theme-sepia', 'dawn-theme-hc', 'dawn-theme-t1e', 'dawn-theme-buckeye', 'dawn-theme-irish', 'dawn-theme-mariners'];
export const applyThemeToDom = (value) => {
  document.documentElement.classList.remove(...THEME_CLASSES);
  if (value && value !== 'standard') document.documentElement.classList.add(`dawn-theme-${value}`);
};
export const applyZoomToDom = (value) => {
  document.documentElement.style.zoom = (!value || value === '100%') ? '' : value;
};

// ── Cached-pref reader for pre-hydration code paths (App.js routing) ─────────
// Reads the GLOBAL-resolution cache — per-DB overrides aren't applied here
// (routing runs before hydration; good enough for defaults).
export const getCachedPref = (prefKey) => {
  try {
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
    return cache?.resolved?.[prefKey] ?? null;
  } catch { return null; }
};

/**
 * Landing path for a db slug, honoring the general.landingPage preference.
 * 'last' uses the per-slug last-visited page tracked by the slug layout.
 */
export const getLandingPath = (slug) => {
  if (!slug) return '/select-database';
  if (getCachedPref('general.landingPage') === 'last') {
    const last = localStorage.getItem(`dawn.lastPage.${slug}`);
    if (last && last !== 'home') return `/${slug}/${last}`;
  }
  return `/${slug}/`;
};

const PreferencesContext = createContext(null);

export const PreferencesProvider = ({ children }) => {
  const { currentUser, realUser, isImpersonating, isSysAdmin } = useAuth();

  // payload = { catalog, rows, defaults, resolved } (server shapes, camelCased by JSON)
  const [payload, setPayload] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [failSoft, setFailSoft] = useState(false);
  const [slugToDbId, setSlugToDbId] = useState({});

  const pendingWrites = useRef(new Map());   // dedupe key → row dto
  const flushTimer = useRef(null);
  const writingMirrors = useRef(false);      // loop guard for dawn-debug-changed echo
  const payloadRef = useRef(null);
  payloadRef.current = payload;

  const catalogByKey = useCallback((p = payloadRef.current) => {
    const map = {};
    (p?.catalog || []).forEach(c => { map[c.prefKey] = c; });
    return map;
  }, []);

  // ── Client-side resolution ──────────────────────────────────────────────
  const resolve = useCallback((prefKey, dbSlug = null) => {
    const p = payloadRef.current;
    if (!p) return getCachedPref(prefKey);
    const def = catalogByKey(p)[prefKey];
    if (!def) return null;

    const slug = (dbSlug ?? localStorage.getItem('pmd-selected-db-slug') ?? '').toLowerCase();
    const dbId = slugToDbId[slug] ?? null;
    const machineId = getMachineId();
    const rows = p.rows || [];

    if (def.allowPerMachine) {
      const m = rows.find(r => r.prefKey === prefKey && r.machineId === machineId);
      if (m) return m.value;
    }
    if (def.allowPerDatabase && dbId != null) {
      const d = rows.find(r => r.prefKey === prefKey && r.databaseInstanceId === dbId);
      if (d) return d.value;
    }
    const g = rows.find(r => r.prefKey === prefKey && r.databaseInstanceId == null && !r.machineId);
    if (g) return g.value;

    // Admin default tiers
    const defaults = p.defaults || [];
    const roles = currentUser?.roles || {};
    const roleInDb = roles[slug] || null;
    const myHighest = ROLE_ORDER.find(o => Object.values(roles).includes(o)) || null;
    const tiers = [
      d => roleInDb && d.databaseInstanceId === dbId && d.role === roleInDb,
      d => myHighest && d.databaseInstanceId == null && d.role === myHighest,
      d => dbId != null && d.databaseInstanceId === dbId && d.role == null,
      d => d.databaseInstanceId == null && d.role == null,
    ];
    for (const match of tiers) {
      const hit = defaults.find(d => d.prefKey === prefKey && match(d));
      if (hit) return hit.value;
    }
    return def.defaultValue;
  }, [catalogByKey, slugToDbId, currentUser]);

  // ── Mirror + visual application ─────────────────────────────────────────
  const applyMirrorsAndVisuals = useCallback(() => {
    const p = payloadRef.current;
    if (!p) return;
    writingMirrors.current = true;
    try {
      Object.entries(MIRROR_KEYS).forEach(([prefKey, lsKey]) => {
        const v = resolve(prefKey);
        if (v != null) localStorage.setItem(lsKey, v);
      });
      applyThemeToDom(resolve('display.theme'));
      applyZoomToDom(resolve('display.uiZoom'));
      DEBUG_OVERLAY_TOOLS.forEach(t => syncOverlayTool(t.key, localStorage.getItem(t.lsKey) === 'true'));
      syncHoverState();
      window.dispatchEvent(new Event('dawn-theme-changed'));
      notifyDebugChanged();
    } finally {
      writingMirrors.current = false;
    }
  }, [resolve]);

  // ── Batched server writes ───────────────────────────────────────────────
  const targetUserId = () => currentUser?.id;

  const flush = useCallback(async () => {
    if (pendingWrites.current.size === 0) return;
    const rows = [...pendingWrites.current.values()];
    pendingWrites.current.clear();
    try {
      await preferencesApi.save(rows, targetUserId());
    } catch (e) {
      // Put failed rows back so the next write retries them; mirrors untouched.
      rows.forEach(r => {
        const k = `${r.prefKey}|${r.databaseInstanceId ?? ''}|${r.machineId ?? ''}`;
        if (!pendingWrites.current.has(k)) pendingWrites.current.set(k, r);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const queueWrite = useCallback((row) => {
    const k = `${row.prefKey}|${row.databaseInstanceId ?? ''}|${row.machineId ?? ''}`;
    pendingWrites.current.set(k, row);
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flush, FLUSH_DEBOUNCE_MS);
  }, [flush]);

  /** Update local rows state optimistically (matches server upsert/delete semantics). */
  const upsertLocalRow = useCallback((row) => {
    setPayload(prev => {
      if (!prev) return prev;
      const rows = [...(prev.rows || [])];
      const idx = rows.findIndex(r =>
        r.prefKey === row.prefKey &&
        (r.databaseInstanceId ?? null) === (row.databaseInstanceId ?? null) &&
        (r.machineId ?? null) === (row.machineId ?? null));
      if (row.value == null) {
        if (idx >= 0) rows.splice(idx, 1);       // null = scope-row deletion
      } else if (idx >= 0) rows[idx] = { ...rows[idx], value: row.value };
      else rows.push(row);
      return { ...prev, rows };
    });
  }, []);

  /**
   * Write-through setter for immediate-write clients (Footer, Sidebar, Header…).
   * scope: 'global' (default) | 'db' | 'machine'.
   * While impersonating: applies locally/visually only — server persistence is
   * suppressed so an admin can't silently rewrite the target's rows. Only the
   * Preferences page Save (savePrefs) writes target rows deliberately.
   */
  const setPref = useCallback((prefKey, value, scope = 'global', dbSlugOverride = null) => {
    const def = catalogByKey()[prefKey];
    const row = { prefKey, databaseInstanceId: null, machineId: null, value: String(value) };
    if (scope === 'machine' && def?.allowPerMachine) row.machineId = getMachineId();
    if (scope === 'db' && def?.allowPerDatabase) {
      const slug = (dbSlugOverride ?? localStorage.getItem('pmd-selected-db-slug') ?? '').toLowerCase();
      const dbId = slugToDbId[slug];
      if (dbId == null) return;                 // unknown db — refuse rather than mis-scope
      row.databaseInstanceId = dbId;
    }
    upsertLocalRow(row);
    const mirror = MIRROR_KEYS[prefKey];
    if (mirror != null) {
      writingMirrors.current = true;
      try { localStorage.setItem(mirror, row.value); } finally { writingMirrors.current = false; }
    }
    if (!isImpersonating && !failSoft) queueWrite(row);
  }, [catalogByKey, slugToDbId, upsertLocalRow, queueWrite, isImpersonating, failSoft]);

  /** Explicit batch save — the Preferences page Save button. Throws on 400. */
  const savePrefs = useCallback(async (rows) => {
    await preferencesApi.save(rows, targetUserId());
    rows.forEach(upsertLocalRow);
    applyMirrorsAndVisuals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsertLocalRow, applyMirrorsAndVisuals, currentUser]);

  const restoreDefaults = useCallback(async (dbId = null) => {
    await preferencesApi.restore(targetUserId(), dbId);
    setPayload(prev => prev ? {
      ...prev,
      rows: (prev.rows || []).filter(r =>
        r.prefKey === 'system.migrated' || (dbId != null && r.databaseInstanceId !== dbId)),
    } : prev);
    setTimeout(applyMirrorsAndVisuals, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyMirrorsAndVisuals, currentUser]);

  // ── Legacy localStorage migration (one-time, guarded) ───────────────────
  const runLegacyMigration = useCallback(async (freshPayload, userId, userIsSysAdmin) => {
    const rows = freshPayload.rows || [];
    const alreadyMigrated = rows.some(r => r.prefKey === 'system.migrated' && r.value === 'true');
    if (rows.length > 0 || alreadyMigrated) return false;

    const lastUser = localStorage.getItem(LAST_USER_KEY);
    if (lastUser && String(lastUser) !== String(userId)) return false;   // someone else's localStorage

    const cat = {};
    (freshPayload.catalog || []).forEach(c => { cat[c.prefKey] = c; });
    const batch = [];
    const addIfDiffers = (prefKey, value, extra = {}) => {
      const def = cat[prefKey];
      if (!def || value == null || value === '' || value === def.defaultValue) return;
      batch.push({ prefKey, databaseInstanceId: null, machineId: null, value: String(value), ...extra });
    };

    addIfDiffers('display.theme', localStorage.getItem('dawn.uiTheme'));
    addIfDiffers('display.uiZoom', localStorage.getItem('dawn.uiZoom'), { machineId: getMachineId() });
    addIfDiffers('general.defaultDatabase', localStorage.getItem('pmd-selected-db-slug'));
    addIfDiffers('downloads.clearMode', localStorage.getItem('dataTransfer.clearMode'));

    // Session timeout: clamp to the caller's legal options — a stored 'never'
    // on a non-admin must not 400 the whole batch.
    let timeout = localStorage.getItem('pmd-security.sessionTimeout');
    if (timeout && !userIsSysAdmin && (timeout === 'never' || parseInt(timeout, 10) > 120)) timeout = null;
    addIfDiffers('security.sessionTimeout', timeout);

    Object.entries(MIRROR_KEYS).forEach(([prefKey, lsKey]) => {
      if (!prefKey.startsWith('debug.')) return;
      addIfDiffers(prefKey, localStorage.getItem(lsKey));
    });

    try {
      const nav = {
        expanded: JSON.parse(localStorage.getItem('pmd-nav-expanded') || '[]'),
        collapsed: localStorage.getItem('pmd-nav-collapsed') === 'true',
      };
      if (nav.expanded.length > 0 || nav.collapsed)
        batch.push({ prefKey: 'general.sidebarNavState', databaseInstanceId: null, machineId: null, value: JSON.stringify(nav) });
    } catch { /* unparseable legacy nav state — skip */ }

    batch.push({ prefKey: 'system.migrated', databaseInstanceId: null, machineId: null, value: 'true' });

    try {
      await preferencesApi.save(batch, userId);
      // Sweep keys belonging to killed preferences
      Object.keys(localStorage)
        .filter(k => SWEEP_PREFIXES.some(p => k.startsWith(p)) || SWEEP_KEYS.includes(k))
        .forEach(k => localStorage.removeItem(k));
      return true;
    } catch { return false; }
  }, []);

  // ── Hydration — driven by the auth payload (GetMe / ResolveAll carry it) ──
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      if (!currentUser?.id) { setHydrated(false); setPayload(null); return; }

      let p = currentUser.preferences ?? null;
      if (!p) {
        try {
          const res = await preferencesApi.get(isImpersonating || !realUser ? currentUser.id : undefined);
          p = res.data;
        } catch { p = null; }
      }
      if (cancelled) return;

      if (!p || !p.catalog) {
        // Fail-soft: no tables / old API / resolver error. Cache + defaults only.
        setFailSoft(true);
        setHydrated(true);
        return;
      }
      setFailSoft(false);

      // Slug → numeric AppSec_Databases.ID map (per-DB scope writes need the
      // numeric id; the payload carries it because /api/databases/available
      // exposes DatabaseName as 'id', not the numeric key). Keyed by both the
      // real slug and lowercased display name — both conventions exist in-app.
      const map = {};
      (p.databases || []).forEach(d => {
        if (d.slug) map[d.slug.toLowerCase()] = d.id;
        if (d.displayName) map[d.displayName.toLowerCase()] = d.id;
      });
      setSlugToDbId(map);

      // One-time legacy import (self only — never while impersonating)
      if (!isImpersonating) {
        const migrated = await runLegacyMigration(p, currentUser.id, !!isSysAdmin);
        if (migrated) {
          try { p = (await preferencesApi.get()).data; } catch { /* keep pre-migration payload */ }
        }
        localStorage.setItem(LAST_USER_KEY, String(currentUser.id));
      }
      if (cancelled) return;

      setPayload(p);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ userId: currentUser.id, resolved: p.resolved || {} }));
      } catch { /* quota — cache is best-effort */ }
      setHydrated(true);
    };
    hydrate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, isImpersonating]);

  // Apply mirrors + visuals whenever the payload lands or the identity swaps
  useEffect(() => {
    if (payload) applyMirrorsAndVisuals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload === null ? 0 : payload.rows, currentUser?.id]);

  // Re-resolve per-DB prefs (theme) when the database changes
  useEffect(() => {
    const onDbChanged = () => applyMirrorsAndVisuals();
    window.addEventListener('pmd-database-changed', onDbChanged);
    return () => window.removeEventListener('pmd-database-changed', onDbChanged);
  }, [applyMirrorsAndVisuals]);

  // Reverse mirror sync: overlays can self-write (classInventory panel close).
  // Diff mirrors against effective values and persist genuine changes.
  useEffect(() => {
    const onDebugChanged = () => {
      if (writingMirrors.current || !payloadRef.current) return;
      Object.entries(MIRROR_KEYS).forEach(([prefKey, lsKey]) => {
        if (!prefKey.startsWith('debug.')) return;
        const mirror = localStorage.getItem(lsKey);
        if (mirror != null && mirror !== resolve(prefKey)) setPref(prefKey, mirror);
      });
    };
    window.addEventListener('dawn-debug-changed', onDebugChanged);
    return () => window.removeEventListener('dawn-debug-changed', onDebugChanged);
  }, [resolve, setPref]);

  // Flush pending writes on unload
  useEffect(() => {
    const onUnload = () => { if (flushTimer.current) clearTimeout(flushTimer.current); flush(); };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [flush]);

  const value = {
    hydrated,
    failSoft,
    catalog: payload?.catalog || [],
    rows: payload?.rows || [],
    slugToDbId,
    getPref: resolve,
    setPref,
    savePrefs,
    restoreDefaults,
    adminRestore: (userId, dbId = null) => preferencesApi.restore(userId, dbId),
    reapplyVisuals: applyMirrorsAndVisuals,
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = () => {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider');
  return ctx;
};

/** Convenience hook: const [theme, setTheme] = usePreference('display.theme'); */
export const usePreference = (prefKey, scope = 'global') => {
  const { getPref, setPref } = usePreferences();
  const value = getPref(prefKey);
  const setter = useCallback(v => setPref(prefKey, v, scope), [prefKey, scope, setPref]);
  return [value, setter];
};
